import * as React from 'react';
import { X } from 'lucide-react';

interface DialogContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextType | null>(null);

export function Dialog({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  return (
    <DialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </DialogContext.Provider>
  );
}

export function DialogContent({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const context = React.useContext(DialogContext);
  if (!context?.open) return null;

  return (
    <div
      className="shadcn-dialog-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) context.onOpenChange(false);
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className={`shadcn-dialog-content ${className}`.trim()} {...props}>
        <button
          type="button"
          className="shadcn-dialog-close-btn"
          onClick={() => context.onOpenChange(false)}
          aria-label="Close dialog"
        >
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`shadcn-dialog-header ${className}`.trim()} {...props}>
      {children}
    </div>
  );
}

export function DialogTitle({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 className={`shadcn-dialog-title ${className}`.trim()} {...props}>
      {children}
    </h2>
  );
}

export function DialogDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`shadcn-dialog-desc ${className}`.trim()} {...props}>
      {children}
    </p>
  );
}

export function DialogFooter({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`shadcn-dialog-footer ${className}`.trim()} {...props}>
      {children}
    </div>
  );
}
